import type { CustomRuleType } from '~/types/loyaltyContract';

export const CUSTOM_RULE_IDENTIFIER_FIELDS = [
  'offerId',
  'packageId',
  'ratePlanId',
  'earnKey',
  'promoKey',
  'productType',
] as const;

/** The identifier kinds a custom rule can be anchored on. */
export type CustomRuleIdentifierType = (typeof CUSTOM_RULE_IDENTIFIER_FIELDS)[number];

export const CUSTOM_RULE_TEXT_IDENTIFIER_FIELDS = [
  'offerId',
  'packageId',
  'ratePlanId',
  'earnKey',
  'promoKey',
] as const satisfies ReadonlyArray<CustomRuleIdentifierType>;

export type CustomRuleTextIdentifierType = (typeof CUSTOM_RULE_TEXT_IDENTIFIER_FIELDS)[number];

export function isCustomRuleTextIdentifier(
  identifierType: CustomRuleIdentifierType,
): identifierType is CustomRuleTextIdentifierType {
  return CUSTOM_RULE_TEXT_IDENTIFIER_FIELDS.includes(identifierType as CustomRuleTextIdentifierType);
}

/**
 * Minimal slice of the create/update form needed to validate required fields
 * client-side before sending a request to svc-lux-loyalty.
 */
export interface CustomRuleFieldInput {
  name?: string;
  startDate?: string;
  type?: CustomRuleType;
  /** Which identifier the user chose to anchor the rule on. */
  selectedIdentifierType: CustomRuleIdentifierType;
  /** The value entered/selected for the chosen identifier (single-create mode). */
  identifierValue?: string | null;
  /**
   * When set (batch create), the identifier is valid only if this list is non-empty.
   * Prefer this over deriving validity from a parsed first element in the dialog.
   */
  batchIdentifierValues?: ReadonlyArray<string>;
  pointsValue?: number;
  pointsMultiplier?: number;
}

/**
 * Validates the required custom-rule form fields client-side so we never send a
 * request that the server is guaranteed to reject (e.g. empty name or identifier
 * value). Mirrors the fields the form marks `required` and keys errors to the
 * field names the dialogs use for inline display.
 *
 * @param input - The relevant form values.
 * @returns A field-keyed error map; empty when all required fields are present.
 */
export function validateCustomRuleFields(input: CustomRuleFieldInput): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!input.name?.trim()) {
    errors.name = 'Name is required';
  }

  if (!input.startDate) {
    errors.startDate = 'Start date is required';
  }

  // The selected identifier must have a value; key the error to that identifier
  // so the relevant field shows it (offerId / packageId / earnKey / promoKey / productType).
  const identifierLabel = CUSTOM_RULE_IDENTIFIER_LABELS[input.selectedIdentifierType];
  if (input.batchIdentifierValues !== undefined) {
    if (input.batchIdentifierValues.length === 0) {
      errors[input.selectedIdentifierType] =
        input.selectedIdentifierType === 'productType'
          ? 'Select at least one product type'
          : `${identifierLabel} is required`;
    }
  } else {
    const identifierValue =
      typeof input.identifierValue === 'string' ? input.identifierValue.trim() : input.identifierValue;
    if (!identifierValue) {
      errors[input.selectedIdentifierType] = `${identifierLabel} is required`;
    }
  }

  // Points fields are required per rule type (matching the conditionally-rendered inputs).
  if (input.type === 'BONUS_POINTS' || input.type === 'BONUS_POINTS_ONLY') {
    if (input.pointsValue == null || Number.isNaN(input.pointsValue)) {
      errors.pointsValue = 'Points value is required';
    }
  } else if (input.type === 'MULTIPLE_ON_BASE_POINTS') {
    if (input.pointsMultiplier == null || Number.isNaN(input.pointsMultiplier)) {
      errors.pointsMultiplier = 'Points multiplier is required';
    }
  }

  return errors;
}

/** Human-readable labels for identifier types, used in error messages. */
export const CUSTOM_RULE_IDENTIFIER_LABELS: Record<CustomRuleIdentifierType, string> = {
  offerId: 'Offer ID',
  packageId: 'Package ID',
  ratePlanId: 'Rate Plan ID',
  earnKey: 'Earn Key',
  promoKey: 'Promo Key',
  productType: 'Product Type',
};
